/**
 * ClinicianAnalytics.tsx — the clinician's own "My stats" dashboard (#21).
 * Every figure THIS FILE renders is a COUNT — no £ / pence / rate here. The
 * tab also mounts the "My earnings" card (EarningsCard.tsx, 2026-08-26 CTO
 * decision: a clinician may see their OWN compensation), which is the ONE
 * deliberate money element on the clinician surface — the money-redaction
 * guard excises that card, then still asserts everything else stays
 * money-free.
 *
 * Composes the two money-free self reads the portal already exposes:
 *  - clinic-service /analytics/me (callbacks, handovers);
 *  - orders-service /analytics/me (approvals, Rx).
 * The two daily trends overlay as grouped bars.
 *
 * UX WAVE 8 §4 (2026-07-21 night): the busiest-times heatmap AND the embedded
 * "My activity log" panel are GONE from this tab — the CTO cut the heatmap
 * from the clinician surface (the admin Analytics panel keeps the company
 * one), and the self-audit view is now its OWN rail section ("My activity",
 * CliniciansPage → MyActivityLog.tsx). The /analytics/activity heatmap reads
 * are therefore no longer fetched here at all.
 */
import { useCallback, useEffect, useState } from 'react';
import { getMyCallbackStats } from '../api/clinic';
import { getMyOrderStats } from '../api/orders';
import type { ClinicianCallbackStats, ClinicianOrderStats } from '../api/types';
import { StatTiles } from '../analytics/StatTiles';
import { TrendBars } from '../analytics/TrendBars';
import { SectionTitle } from '../staff/ui';
import { Card, ErrorState, Spinner } from '../ui/kit';
import { EarningsCard } from './EarningsCard';

interface Loaded {
  callbacks: ClinicianCallbackStats;
  orders: ClinicianOrderStats;
}

/** Overlay callbacks/day + approvals/day onto one sorted date axis. */
function trendFrom(callbacks: ClinicianCallbackStats, orders: ClinicianOrderStats) {
  const dates = [...new Set([...callbacks.perDay.map((d) => d.date), ...orders.perDay.map((d) => d.date)])].sort();
  const cbByDate = new Map(callbacks.perDay.map((d) => [d.date, d.callbacks]));
  const apByDate = new Map(orders.perDay.map((d) => [d.date, d.approvals]));
  return {
    labels: dates,
    series: [
      { label: 'Callbacks worked', color: 'var(--gps-primary)', values: dates.map((d) => cbByDate.get(d) ?? 0) },
      { label: 'Orders approved', color: 'var(--gps-primary-dark, var(--gps-green-text))', values: dates.map((d) => apByDate.get(d) ?? 0) },
    ],
  };
}

export function ClinicianAnalytics() {
  const [data, setData] = useState<Loaded | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(() => {
    setStatus('loading');
    Promise.all([getMyCallbackStats(), getMyOrderStats()])
      .then(([callbacks, orders]) => {
        setData({ callbacks, orders });
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The earnings card loads independently (its own fetch/error state) so a
  // stats failure never hides the money answer, and vice versa.
  if (status === 'loading') {
    return (
      <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
        <EarningsCard />
        <Spinner label="Loading your stats…" />
      </div>
    );
  }
  if (status === 'error' || !data) {
    return (
      <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
        <EarningsCard />
        <ErrorState message="Sorry — we could not load your stats." onRetry={load} />
      </div>
    );
  }

  const { callbacks, orders } = data;
  const trend = trendFrom(callbacks, orders);
  const windowHint = `last ${callbacks.windowDays} days`;

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
      <EarningsCard />
      <Card>
        <SectionTitle>Your activity</SectionTitle>
        <StatTiles
          items={[
            { label: 'Callbacks worked', value: callbacks.totals.callbacksHandled, hint: windowHint },
            { label: 'Converted', value: callbacks.totals.converted, hint: windowHint },
            { label: 'Handovers sent', value: callbacks.totals.handovers, hint: windowHint },
            { label: 'Orders approved', value: orders.totals.approved + orders.totals.preapproved, hint: windowHint },
            { label: 'Orders rejected', value: orders.totals.rejected, hint: windowHint },
            { label: 'Prescriptions issued', value: orders.totals.prescriptionsIssued, hint: windowHint },
            { label: 'Pending callbacks', value: callbacks.pendingNow, hint: 'open + in progress now' },
          ]}
        />
      </Card>

      <Card>
        <SectionTitle>How it varies day to day</SectionTitle>
        <TrendBars labels={trend.labels} series={trend.series} ariaLabel={`Your callbacks worked and orders approved per day over the ${windowHint}`} />
      </Card>

      <Card>
        <SectionTitle>Call outcomes</SectionTitle>
        <StatTiles
          items={[
            { label: 'Answered', value: callbacks.outcomes.answered },
            { label: 'No answer', value: callbacks.outcomes.no_answer },
            { label: 'Voicemail', value: callbacks.outcomes.voicemail },
            { label: 'Busy', value: callbacks.outcomes.busy },
            { label: 'Disconnected', value: callbacks.outcomes.disconnected },
          ]}
        />
      </Card>
    </div>
  );
}
